import { Grid, Box, Container, Typography } from '@mui/material'
import MuiButton from 'gui/components/common/MuiButton'
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Map, List } from 'immutable'
import VideoPlayer from 'gui/components/video-player'
import ImmutablePropTypes from 'react-immutable-proptypes'
import Accordion from 'gui/components/accordion'
import Link from 'gui/components/link'
import MediaAutoComplete from 'gui/components/media-auto-complete'
import { MEDIA_STORE_KEY } from 'services/media'
import { getPlayableMediaAssetStream } from 'services/media-assets/view'
import { MEDIA_ASSET_PROVIDER_BRIGHTCOVE_STUDIO_VIDEOS } from 'services/media-assets/provider-urls'
import { getDataList as getDataListAction } from 'services/data/redux/actions'
import { MEDIA_ASSETS_STORE_KEY } from 'services/media-assets'
import {
  MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_SUBTITLE,
  MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION,
} from 'services/media-assets/reference-types'
import { bindActionCreators } from 'redux'
import { connect as connectRedux, useDispatch } from 'react-redux'
import { compose } from 'recompose'
import { getDateWithoutTime } from './helpers'
import './index.scss'
import { TYPE_BRIGHTCOVE_STUDIO } from '../link/types'
import { getVideoUrl, isMediaPlayable } from './mediaUtils'

/**
 * Renders a single media field with autocomplete, preview, and metadata.
 */
function MediaField ({
  name,
  mediaValue,
  onChange,
  disabled,
  data,
  mediaRecords,
  mediaInfoRecords,
  clearMediaField,
}) {
  const [notPlayable, setNotPlayable] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  const mediaId = mediaValue
  const media = mediaId ? mediaRecords.get(mediaId) || Map() : Map()
  const mediaInfo = mediaId ? mediaInfoRecords.get(mediaId) || Map() : Map()
  const contentId = media ? media.get('contentId') || '' : ''

  // Get media assets for subtitles/captions with safe fallbacks
  const mediaAssetsList = data.getIn([MEDIA_ASSETS_STORE_KEY, 'records']) || Map()
  const mediaAssetsForCurrentMedia = mediaId
    ? mediaAssetsList.valueSeq().filter((asset) => asset && asset.get('mediaId') === mediaId)
    : List()

  // Extract subtitles and captions with safe filtering
  const subtitles = mediaAssetsForCurrentMedia
    .filter((v) => v && v.get('mediaReferenceType') === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_SUBTITLE)
    .toList()

  const captions = mediaAssetsForCurrentMedia
    .filter(
      (v) => v && v.get('mediaReferenceType') === MEDIA_ASSETS_MEDIA_REFERENCE_TYPE_CLOSED_CAPTION,
    )
    .toList()

  useEffect(() => {
    if (!mediaId) {
      setNotPlayable(false)
      return
    }
    const videoUrl = getVideoUrl(mediaInfo.get('mediaUrls'))
    setNotPlayable(!isMediaPlayable(media, videoUrl))
  }, [mediaId, media, mediaInfo])

  useEffect(() => {
    setSearchValue(contentId)
    setIsSearching(false)
    setSuggestions([])
  }, [contentId])

  const dispatch = useDispatch()

  // Fetch media assets for subtitles/captions when mediaId changes
  useEffect(() => {
    if (mediaId) {
      dispatch(
        getDataListAction({
          storeKey: MEDIA_ASSETS_STORE_KEY,
          options: Map({ mediaId }),
        }),
      )
    }
  }, [mediaId, dispatch])

  const handleInputChange = (e) => {
    setSearchValue(e.target.value)
    setIsSearching(true)
    setLoading(true)
    dispatch(
      getDataListAction({
        storeKey: MEDIA_STORE_KEY,
        processing: true,
        options: Map({ contentId: e.target.value }),
      }),
    )
  }

  useEffect(() => {
    if (!isSearching || !searchValue) {
      setSuggestions([])
      setLoading(false)
      return
    }
    const records = data.getIn(['media', 'records']) || Map()
    const allSuggestions = records
      .valueSeq()
      .filter((record) => {
        const cid = record.get('contentId') || ''
        return cid.toLowerCase().includes(searchValue.toLowerCase())
      })
      .map((record) => ({
        contentId: record.get('contentId'),
        mediaId: record.get('mediaId'),
      }))
      .toArray()
    setSuggestions(allSuggestions)
    setLoading(false)
  }, [data, searchValue, isSearching])

  const handleSuggestionSelect = (event, record) => {
    if (record && record.mediaId) {
      onChange({ target: { name, value: record.mediaId } }, record)
      setSearchValue(record.contentId)
      setSuggestions([])
      setIsSearching(false)
      dispatch(
        getDataListAction({
          storeKey: MEDIA_STORE_KEY,
          processing: true,
          options: Map({ mediaId: record.mediaId }),
        }),
      )
    } else {
      onChange({ target: { name, value: null } })
      setSearchValue('')
      setSuggestions([])
      setIsSearching(false)
    }
  }

  return (
    <div key={`content-media-${name}`}>
      {/* Search bar spanning full width */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <MediaAutoComplete
            className="content-detail__input"
            value={searchValue}
            name={name}
            onChange={handleInputChange}
            onSuggestionSelect={handleSuggestionSelect}
            disabled={disabled}
            placeholder={'Search or select media...'}
            suggestions={suggestions}
            loading={loading}
          />
        </Grid>
      </Grid>
      {mediaId && notPlayable && !loading && (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <div
              className="content-detail__section warning"
              style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeeba',
                color: '#856404',
                padding: '12px',
                marginTop: '8px',
                borderRadius: '4px',
              }}
            >
              ⚠️ Warning: This media is not playable. Please ensure the media has valid video URLs
              before publishing.
            </div>
          </Grid>
        </Grid>
      )}
      {/* Main two-column layout: video left, metadata right */}
      {mediaId && !loading && (
        <Grid container spacing={3} alignItems="flex-start">
          {/* Left: Video */}
          <Grid item xs={12} md={7} lg={8}>
            <VideoPlayer
              src={getVideoUrl(mediaInfo.get('mediaUrls'))}
              srcTextTrackThumnail={mediaInfo.getIn(['textTracks', 'thumbnail'])}
              subtitles={subtitles}
              captions={captions}
            />
          </Grid>
          {/* Right: Metadata (smaller) */}
          <Grid item xs={12} md={5} lg={4}>
            <Box className="modern-info-box">
              {/* Metadata fields as label-value pairs */}
              {[
                {
                  label: 'Media ID',
                  value: (
                    <Link absolute={true} href={`/media/${mediaId}`}>
                      {mediaId}
                    </Link>
                  ),
                },
                {
                  label: 'BC Video ID',
                  value: mediaInfo.get('providerKey') && (
                    <Link
                      className="media-detail__provider-link"
                      type={TYPE_BRIGHTCOVE_STUDIO}
                      href={`${MEDIA_ASSET_PROVIDER_BRIGHTCOVE_STUDIO_VIDEOS}/${mediaInfo.get('providerKey')}`}
                    >
                      {mediaInfo.get('providerKey')}
                    </Link>
                  ),
                },
                {
                  label: 'Media Maestro Code',
                  value: media.get('mediaMaestroTitleCode'),
                },
                {
                  label: 'Policy ID',
                  value: media.get('policyId'),
                },
                {
                  label: 'Video Updated at',
                  value: getDateWithoutTime(media.get('updatedAt')),
                },
                {
                  label: 'Duration',
                  value: getPlayableMediaAssetStream({ data, mediaId })?.get('duration'),
                },
              ].map(({ label, value }) => (
                <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }} key={label}>
                  <Grid
                    item
                    xs={5}
                    md={5}
                    sx={{ fontWeight: 500, color: '#555', textAlign: 'left' }}
                  >
                    {label}
                  </Grid>
                  <Grid item xs={7} md={7} sx={{ pl: 2 }}>
                    {value || <span style={{ color: '#aaa' }}>—</span>}
                  </Grid>
                </Grid>
              ))}
              {/* Remove Button */}
              <Box sx={{ textAlign: 'right', mt: 2 }}>
                <MuiButton
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    onChange({ target: { name, value: null } })
                    clearMediaField(name)
                    setNotPlayable(false)
                  }}
                >
                  Remove Media
                </MuiButton>
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}
    </div>
  )
}

/**
 * VideoContentMedia
 * Renders media selection and preview UI for content detail forms.
 * Handles autocomplete, media validation, and metadata display for preview and feature media.
 */
function VideoContentMedia (props) {
  const { data, disabled, onChange, values } = props

  if (!data || typeof data.getIn !== 'function') return null

  const mediaRecords = data.getIn(['media', 'records']) || Map()
  const mediaInfoRecords = data.getIn(['mediaInfo', 'records']) || Map()

  const previewMediaId = values.previewMediaId || null
  const featureMediaId = values.featureMediaId || null

  const clearMediaField = (name) => {
    onChange({ target: { name, value: null } })
  }

  // Hydrate media info for both fields on page load/refresh
  const dispatch = useDispatch()
  useEffect(() => {
    ;['previewMediaId', 'featureMediaId'].forEach((name) => {
      const mediaId = values && values[name]
      if (mediaId && !mediaRecords.get(mediaId)) {
        dispatch(
          getDataListAction({
            storeKey: MEDIA_STORE_KEY,
            options: { mediaId },
          }),
        )
      }
    })
  }, [values, dispatch, mediaRecords])

  const [show, toggleShow] = useState(true)

  return (
    <Container className="content-detail__section media-information" maxWidth={false}>
      <Accordion title="Media Information" toggleShow={() => toggleShow(!show)} show={show} />
      {show && (
        <React.Fragment>
          <Typography
            variant="h6"
            sx={{
              mt: 3,
              mb: 2,
              color: '#333',
              fontWeight: 600,
              borderBottom: '2px solid #e0e0e0',
              pb: 1,
            }}
          >
            Preview Media
          </Typography>
          <MediaField
            key="previewMediaId"
            name="previewMediaId"
            mediaValue={previewMediaId}
            onChange={onChange}
            disabled={disabled}
            data={data}
            mediaRecords={mediaRecords}
            mediaInfoRecords={mediaInfoRecords}
            clearMediaField={clearMediaField}
          />
          <Typography
            variant="h6"
            sx={{
              mt: 4,
              mb: 2,
              color: '#333',
              fontWeight: 600,
              borderBottom: '2px solid #e0e0e0',
              pb: 1,
            }}
          >
            Feature Media
          </Typography>
          <MediaField
            key="featureMediaId"
            name="featureMediaId"
            mediaValue={featureMediaId}
            onChange={onChange}
            disabled={disabled}
            data={data}
            mediaRecords={mediaRecords}
            mediaInfoRecords={mediaInfoRecords}
            clearMediaField={clearMediaField}
          />
        </React.Fragment>
      )}
    </Container>
  )
}

VideoContentMedia.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  values: PropTypes.object,
}

VideoContentMedia.defaultProps = {
  disabled: false,
}

export default compose(
  connectRedux(
    (state) => ({ data: state.data }),
    (dispatch) => bindActionCreators({ getDataList: getDataListAction }, dispatch),
  ),
)(VideoContentMedia)
